import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, renderSlot as _renderSlot, vShow as _vShow, createElementBlock as _createElementBlock, withDirectives as _withDirectives, Transition as _Transition, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "strategy-brief-hub-step-layout" }
const _hoisted_2 = { slot: "template-main" }
const _hoisted_3 = { "no-margin": "" }

import {useNavigationStrategyService} from '@/brief-hub/brief/NavigationStrategyService';
    import {RouteNames} from '@/brief-hub/router/route-names';
    import {HttpServiceResponse, HttpError} from 'vue3-starter/src/typescript/http/http';
    import UserResource from '@/brief-hub/user/UserResource';
    import {computed, ComputedRef} from 'vue';
    import {useStore} from 'vuex';
    import {useRoute} from 'vue-router';
    import {useTranslation} from '@malt/vue3-use';
    import router from '@/brief-hub/router';
    import {useConnectedUserStore} from '@/brief-hub/stores/connectedUser.store';
    import {useBriefStrategyStore} from '@/brief-hub/stores/briefStrategy.store';
    import {BriefStrategyEligibilityRejectionResource, StrategyDraftStateWrapper} from '@/common/api/api-raw-types';
    import {useStrategyCurrentStep} from '@/brief-hub/strategy/deposit/progress-tracker/useStrategyCurrentStep';
    import {pushVJoySnackbar, VJoyButton} from '@maltjoy/core-vue';
    import {storeToRefs} from 'pinia';
    import StrategyBriefResource from '@/brief-hub/brief/StrategyBriefResource';
    import {useMediaQuery} from '@malt/joy-entrypoint/src/vue/use';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'StrategyBriefHubStepLayout',
  props: {
        hasSidebar: {type: Boolean, default: false},
        hasFooter: {type: Boolean, default: true},
        displaySkipButton: {type: Boolean, default: false},
    },
  setup(__props) {

    const connectedUserStore = useConnectedUserStore();
    const briefStrategyStore = useBriefStrategyStore();
    const {brief, saving, redirectionReason, originReferrer} = storeToRefs(briefStrategyStore);
    const {currentStepNumber, currentSubStepNumber} = useStrategyCurrentStep();

    const props = __props;
    const {previousRoute, nextIncompleteRoute, nextRoute} = useNavigationStrategyService();
    const isFormValidAndFilled: ComputedRef = computed<boolean>(() => !!briefStrategyStore.payload);
    const user = computed<UserResource | null>(() => connectedUserStore.user);
    const $route = useRoute();

    const {msg} = useTranslation();
    let loading = false;

    function init(): void {
        document.title = msg('customer.brief.exec.form.page.title');
        saving.value = false;
    }

    const briefComplete = computed<boolean>(() => {
        return brief.value
            ? (brief.value.draftState as StrategyDraftStateWrapper).strategyDraftState === 'MISSION_DESCRIPTION_SECTION_COMPLETED'
            : false;
    });

    const showBackToOverviewButton = computed<boolean>(() => briefComplete.value && $route.name !== RouteNames.STRATEGY_BRIEF_OVERVIEW);

    const previousAvailable = computed<boolean>(() => {
        // for the unlogged funnel we prevent the user to go back to the brief home screen
        return !briefComplete.value && !!computedPreviousRoute.value && !!user.value && !(isMobile.value && briefComplete.value);
    });

    const showNext = computed<boolean>(() => {
        return !briefComplete.value && !props.displaySkipButton && !(isMobile.value && briefComplete.value);
    });

    const computedPreviousRoute = computed<string | null>(() => {
        if (!brief.value || !$route.name) {
            return null;
        }
        return previousRoute($route.name as string) || null;
    });

    const {matchMedia} = useMediaQuery();
    const isMobile = computed(() => matchMedia.value.max.Xs);

    function goToPreviousStep(): void {
        const previousRouteComputed = computedPreviousRoute.value || RouteNames.NOT_FOUND;
        router.push({name: previousRouteComputed, params: {id: brief.value?.briefId ?? ''}});
    }

    async function saveBrief(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        loading = true;
        const [data, error] = await briefStrategyStore.saveStrategyBrief();
        loading = false;
        if (!data || error) {
            handleSaveBriefError(error);
        }
        return [data, error];
    }

    function handleSaveBriefError(error: HttpError | null) {
        if (
            ($route.name === RouteNames.STRATEGY_BRIEF_NEW || $route.name === RouteNames.STRATEGY_BRIEF_FREELANCER_PROFILE) &&
            error?.response?.status === 422
        ) {
            const rejectResponse = error.response.data as BriefStrategyEligibilityRejectionResource;
            redirectionReason.value = rejectResponse.reason;
            router.push({
                name: RouteNames.STRATEGY_BRIEF_REDIRECTION_REGULAR,
            });
        } else {
            pushVJoySnackbar({
                props: {
                    message: msg('customer.brief.error.unknown'),
                    level: 'error',
                },
            });
        }
    }

    async function onFormSubmit(event: Event): Promise<void> {
        if (!isFormValidAndFilled.value) {
            return;
        }
        const [, error] = await saveBrief();

        if (!error && brief.value?.briefId) {
            const next = nextIncompleteRoute(brief.value.draftState as StrategyDraftStateWrapper);
            const nextLogical = nextRoute($route.name);

            router.push({
                name: $route.name === RouteNames.STRATEGY_BRIEF_NEW ? next : nextLogical,
                params: {id: brief.value.briefId},
            });
        }
    }

    async function backToOverview(): Promise<void> {
        const [, error] = await saveBrief();
        if (!error && brief) {
            router.push({
                name: RouteNames.STRATEGY_BRIEF_OVERVIEW,
                params: {
                    id: brief.value?.briefId,
                    link: ($route.name as string) || '',
                },
            });
        }
    }

    function goToReferrer(): void {
        if ($route.query.referrer) {
            window.location.href = $route.query.referrer;
        } else if (originReferrer?.value) {
            window.location.href = originReferrer.value;
        } else {
            // Have to refresh page to prevent funnel mode, so use window.location instead of $router.push().
            window.location.href = '/project-proposal-summaries';
        }
    }

    const store = useStore();
    const connectedUser = computed((): UserResource => store.getters.connectedUser!);

    const isUnloggedOrStandardUser = computed<boolean>(() => {
        return !connectedUser.value || connectedUser.value?.serviceLevelType === 'STANDARD';
    });

    function upgradeServiceLevel(): void {
        const pageSource = 'blocker';
        const sourceComponent = 'brief_top_bar_upgrade_button';
        const redirectUrl = `/customer/searches${$route.path}`;
        window.location.href = `/client-settings/service-level?pageSource=${pageSource}&sourceComponent=${sourceComponent}&successRedirectPath=${redirectUrl}&dropRedirectPath=${redirectUrl}`;
    }

    init();

return (_ctx: any,_cache: any) => {
  const _component_VJoyHeader = _resolveComponent("VJoyHeader")!
  const _component_VJoyStep = _resolveComponent("VJoyStep")!
  const _component_VJoyStepper = _resolveComponent("VJoyStepper")!
  const _component_VJoyFunnelFooter = _resolveComponent("VJoyFunnelFooter")!
  const _component_VJoyTemplate = _resolveComponent("VJoyTemplate")!
  const _component_VJoyWrapper = _resolveComponent("VJoyWrapper")!
  const _component_VJoyFunnel = _resolveComponent("VJoyFunnel")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_VJoyFunnel, {
      "layout-width": __props.hasSidebar ? 'large' : 'medium',
      profile: "strategy"
    }, {
      "funnel-header": _withCtx(() => [
        _createVNode(_component_VJoyHeader, null, {
          "header-left": _withCtx(() => [
            _createVNode(_unref(VJoyButton), {
              slot: "header-left",
              icon: "cross",
              variant: "ghost",
              circle: "",
              onClick: goToReferrer
            })
          ]),
          "header-right": _withCtx(() => [
            (isUnloggedOrStandardUser.value)
              ? (_openBlock(), _createBlock(_unref(VJoyButton), {
                  key: 0,
                  onClick: upgradeServiceLevel
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$msg('customer.brief.upgrade.service.level')), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      "funnel-stepper": _withCtx(() => [
        _createVNode(_component_VJoyStepper, {
          step: _unref(currentStepNumber),
          "sub-step": _unref(currentSubStepNumber)
        }, {
          default: _withCtx(() => [
            _createVNode(_component_VJoyStep, { "sub-steps": 5 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$msg('customer.brief.exec.progress-tracker.profile')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_VJoyStep, { "sub-steps": 4 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$msg('customer.brief.exec.progress-tracker.scope')), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["step", "sub-step"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_component_VJoyWrapper, {
            direction: "column",
            align: "stretch",
            "no-margin": ""
          }, {
            default: _withCtx(() => [
              _createVNode(_component_VJoyTemplate, {
                sidebar: __props.hasSidebar ? 'right' : undefined,
                class: "strategy-layout__template"
              }, _createSlots({
                "template-title": _withCtx(() => [
                  _createVNode(_Transition, { name: "slide-fade" }, {
                    default: _withCtx(() => [
                      _withDirectives((_openBlock(), _createElementBlock("h1", {
                        key: _unref($route).path,
                        class: "step__main-title"
                      }, [
                        _renderSlot(_ctx.$slots, "title")
                      ])), [
                        [_vShow, _unref($route).path]
                      ])
                    ]),
                    _: 3
                  })
                ]),
                "template-main": _withCtx(() => [
                  _createElementVNode("div", _hoisted_2, [
                    _renderSlot(_ctx.$slots, "default")
                  ]),
                  (__props.hasFooter)
                    ? (_openBlock(), _createBlock(_component_VJoyFunnelFooter, { key: 0 }, {
                        "footer-left": _withCtx(() => [
                          (previousAvailable.value)
                            ? (_openBlock(), _createBlock(_unref(VJoyButton), {
                                key: 0,
                                label: _ctx.$msg('customer.brief.exec.form.link.previous'),
                                variant: "secondary",
                                onClick: goToPreviousStep
                              }, null, 8, ["label"]))
                            : _createCommentVNode("", true)
                        ]),
                        "footer-right": _withCtx(() => [
                          (showBackToOverviewButton.value)
                            ? (_openBlock(), _createBlock(_unref(VJoyButton), {
                                key: 0,
                                slot: "footer-right",
                                label: _ctx.$msg('general.validate'),
                                disabled: !isFormValidAndFilled.value || _unref(loading),
                                size: isMobile.value ? 'small' : 'medium',
                                onClick: backToOverview
                              }, null, 8, ["label", "disabled", "size"]))
                            : _createCommentVNode("", true),
                          (showNext.value)
                            ? (_openBlock(), _createBlock(_unref(VJoyButton), {
                                key: 1,
                                slot: "footer-right",
                                label: _ctx.$msg('customer.brief.exec.form.link.next'),
                                variant: "main",
                                class: _normalizeClass({next: !previousAvailable.value}),
                                disabled: !isFormValidAndFilled.value || _unref(loading),
                                "data-testid": "search_plus_brief_next",
                                onClick: onFormSubmit
                              }, null, 8, ["label", "class", "disabled"]))
                            : _createCommentVNode("", true),
                          (__props.displaySkipButton && !(isMobile.value && briefComplete.value))
                            ? (_openBlock(), _createBlock(_unref(VJoyButton), {
                                key: 2,
                                slot: "footer-right",
                                label: _ctx.$msg('customer.brief.exec.form.link.skip'),
                                variant: "ghost",
                                size: "small",
                                class: _normalizeClass({next: !previousAvailable.value}),
                                "data-testid": "search_plus_brief_skip",
                                onClick: onFormSubmit
                              }, null, 8, ["label", "class"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, [
                (__props.hasSidebar)
                  ? {
                      name: "template-sidebar",
                      fn: _withCtx(() => [
                        _createElementVNode("div", _hoisted_3, [
                          _renderSlot(_ctx.$slots, "right")
                        ])
                      ]),
                      key: "0"
                    }
                  : undefined
              ]), 1032, ["sidebar"])
            ]),
            _: 3
          })
        ])
      ]),
      _: 3
    }, 8, ["layout-width"])
  ]))
}
}

})